import React, { useState, useRef, useEffect } from 'react';

import Papa from 'papaparse';
import { Dropdown } from '@fluentui/react';
import Plot from 'react-plotly.js';

const PumpAnalysis = () => {
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({"Missing": "FALSE", "Transect": "PRAIRIE ELK COLONY 1 - Intake"});
  const [filteredData, setFilteredData] = useState([]);

  // Load and parse CSV
  useEffect(() => {
    fetch('https://hydroviz.s3.us-west-2.amazonaws.com/missouri-2024/Pump+Analysis+2005-2024.csv')
      .then(res => res.text())
      .then(text => {
        Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          complete: (result) => {
            setData(result.data);
            setFilteredData([]); // Initially, no filters applied
          },
        });
      });
  }, []);

  // Apply filters whenever they change
  useEffect(() => {
    let result = data;

    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        result = result.filter(item =>
          item[key] === value
        );
      }
    });

    setFilteredData(result);
  }, [filters, data]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const columns = data.length > 0 ? Object.keys(data[0]) : [];

  const locations = React.useMemo(() => {
    if(data.length === 0) return [];
    let arr = [];
    data.forEach(res => {
      if(arr.includes(res.Transect)) return;
      arr.push(res.Transect);
    });
    return arr;
  }, [data]);

  const computeSliceChange = (data) => {
    if (data.length < 2) return [];
  
    let changes = [{ date: data[0].Date, change: 0 }];
  
    for (let i = 1; i < data.length; i++) {
      const prev = parseFloat(data[i - 1].Slice);
      const curr = parseFloat(data[i].Slice);
      changes.push({
        date: data[i].Date,
        change: curr - prev,
      });
    }

    console.log(changes);
  
    return changes;
  };

  const getAnnualChangeStats = (sliceChanges) => {
    const annualData = {};
  
    sliceChanges.forEach(({ date, change }) => {
      const year = new Date(date).getFullYear();
      if (!annualData[year]) {
        annualData[year] = [];
      }
      annualData[year].push(change);
    });
  
    const stats = Object.entries(annualData).map(([year, changes]) => {
      const min = Math.min(...changes);
      const max = Math.max(...changes);
      const avg =
        changes.reduce((sum, val) => sum + val, 0) / changes.length;
  
      return {
        year: parseInt(year),
        min: parseFloat(min.toFixed(2)),
        max: parseFloat(max.toFixed(2)),
        avg: parseFloat(avg.toFixed(2)),
      };
    });
  
    return stats.sort((a, b) => a.year - b.year); // optional: sort by year
  };

  const getRateOfChangeSeries = (data) => {
    const result = [];
  
    for (let i = 1; i < data.length; i++) {
      const prev = parseFloat(data[i - 1].Slice);
      const curr = parseFloat(data[i].Slice);
      const datePrev = new Date(data[i - 1].Date);
      const dateCurr = new Date(data[i].Date);
  
      const daysDiff = (dateCurr - datePrev) / (1000 * 60 * 60 * 24);
  
      result.push({
        date: data[i].Date,
        rate: daysDiff !== 0 ? (curr - prev) / daysDiff : 0
      });
    }
  
    return result;
  };

  const sliceChanges = computeSliceChange(filteredData);
  const annualStats = getAnnualChangeStats(sliceChanges);
  const rateOfChange = getRateOfChangeSeries(filteredData);
  // console.table(annualStats);

  return (
    <div>
      <h2>Pump Analysis Data</h2>
      {/* Filters */}
      <div>
        <Dropdown 
          label='Selected Transect' 
          options={locations.map(loc => ({key: loc, text: loc}))} 
          selectedKey={filters["Transect"]} 
          onChange={(e, o) => handleFilterChange("Transect", o.key)} />
      </div>


        {/* Plot */}
        {filteredData.length > 0 && (
          <Plot
            useResizeHandler
            data={[
              {
                x: filteredData.map(d => d.Date),
                y: filteredData.map(d => parseFloat(d.Slice)), // Ensure slice is a number
                type: 'scatter',
                mode: 'markers+lines',
                marker: { size: 8 },
              },
            ]}
            layout={{
              title: filteredData[0]?.Transect || 'Transect',
              xaxis: { 
                title: 'Date', 
                dtick: 'M12',
                type: 'date'
              },
              yaxis: { title: 'Length (ft.)' },
              shapes: [
                {type: 'rect', x0: '2011-01-01', x1: '2011-12-31', xref: 'x', y0: 0, y1: 1, yref: 'paper', fillcolor: 'rgba(200, 200, 255, 0.3)', layer: 'below', line: {width: 0}},

                {type: 'rect', x0: '2019-01-01', x1: '2019-12-31', xref: 'x', y0: 0, y1: 1, yref: 'paper', fillcolor: 'rgba(200, 200, 255, 0.3)', layer: 'below', line: {width: 0}}
              ]
            }}
            style={{ width: '100%', height: '500px' }}
          />
        )}

        {sliceChanges.length > 0 && (
          <Plot
            useResizeHandler
            data={[
              {
                x: sliceChanges.map(d => d.date),
                y: sliceChanges.map(d => d.change),
                type: 'scatter',
                mode: 'lines+markers',
                marker: { color: 'orange' },
                name: 'Slice Δ'
              }
            ]}
            layout={{
              title: 'Change Over Time',
              xaxis: { title: 'Date', dtick: 'M12', },
              yaxis: { title: 'Ft. change over time' }
            }}
            style={{ width: '100%', height: '500px'}}
          />
        )}

        {/* {annualStats.length > 0 && (
          <Plot
            data={[
              {
                x: annualStats.map(stat => stat.year),
                y: annualStats.map(stat => stat.min),
                type: 'bar',
                name: 'Min Change',
              },
              {
                x: annualStats.map(stat => stat.year),
                y: annualStats.map(stat => stat.max),
                type: 'bar',
                name: 'Max Change',
              },
              {
                x: annualStats.map(stat => stat.year),
                y: annualStats.map(stat => stat.avg),
                type: 'bar',
                name: 'Avg Change',
              }
            ]}
            layout={{
              title: 'Annual Slice Change Summary',
              barmode: 'group',
              xaxis: { title: 'Year' },
              yaxis: { title: 'Slice Change' }
            }}
            style={{ width: '100%', height: '500px'}}
          />
        )} */}

        {rateOfChange.length > 0 && (
          <Plot
            data={[
              {
                x: rateOfChange.map(d => d.date),
                y: rateOfChange.map(d => d.rate),
                type: 'scatter',
                mode: 'lines+markers',
                marker: { color: 'green' },
                name: 'Rate of Change (per day)'
              }
            ]}
            layout={{
              title: 'Slice Rate of Change Over Time',
              xaxis: { title: 'Date', dtick: 'M12' },
              yaxis: { title: 'Rate of Change (slice/day)' }
            }}
            style={{ width: '100%', height: '400px'}}
          />
        )}


      {/* Display Filtered Data */}
      <table border="1" cellPadding="5">
        <thead>
          <tr>
            {columns.map(col => <th key={col}>{col}</th>)}
            <th>Slice Diff From Previous</th>
          </tr>
        </thead>
        <tbody>
          {filteredData.map((row, i) => (
            <tr key={i}>
              {columns.map(col => <td key={col}>{row[col]}</td>)}
              <td>{i > 0 ? filteredData[i-1]["Slice"] - row["Slice"] : "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PumpAnalysis;